// tslint:disable: max-file-line-count
import { Checkbox } from '@cian/checkbox-component';
import { DropdownField, Label } from '@cian/components';
import { Popup } from '@cian/popup-component';
import { TextField } from '@cian/text-field-component';
import { Tooltip, TooltipPosition } from '@cian/tooltip-component';
import { mergeStyles } from '@cian/utils';
import { PushManager, PopupManager, EMessageResponseTypes } from '@cian/push-manager';
import { IHttpApi } from '@cian/http-api/shared';
import * as React from 'react';

import { IRequestSubscribeNewResponse, requestSubscribeNew } from '../../../../api/subscribe';
import { Button } from '../../../../common/components/button';
import { CloseBtn } from '../../../../common/components/close_button';
import { ModalWrapper } from '../../../../common/components/modal/wrapper';
import { IJsonQuery } from '../../../../json_query';
import { validateEmailFormat } from '../../../../utils/format';
import { SavedSearchModal } from '../SavedSearchModal';
import {
  trackingSaveFilterSend,
  trackSubscribed,
  trackingSubscribeWithFlag,
  trackNewAccountRegistration,
  trackSaveButtonWithAdvancedFilterClick,
} from '../tracking';
import { TDealType } from './container';
import { EmailTooltip } from './EmailTooltip';
import { License } from './License';
import * as style from './index.css';

export interface ISavePopupProps {
  httpApi: IHttpApi;
  userEmail: string | null;
  pageHeader: string | null;
  jsonQuery: IJsonQuery;
  dealType: TDealType;
  queryString: string;
  isAuthenticated: boolean;
  showPushNotification: boolean | undefined;
  metaHeader?: string;
  isAdvancedFiltersOpened?: boolean;
  isPushEnabled: boolean;
  baseUrl: string;
  currentSubdomain: string;
  applicationServerKey?: string;
  notificationPermission: string;

  onClose(): void;
  onOffersSearch(): void;
}

export interface ISavePopupState {
  title: string;
  titleInvalid: boolean;
  email: string;
  emailInvalid: boolean;
  emailError: string;
  isNews: boolean;
  isLoading: boolean;
  isTooltipOpen: boolean;
  isSuccess: boolean;
  isPushCheck: boolean;
  notificationFrequency: TNotificationFrequencyValue;
  isModalVisible: boolean;
  isSubscribed: boolean;
}

export type TNotificationFrequencyValue = 1 | 2 | 3;
export type TNotificationFrequency = 'everyDay' | 'everyHour' | 'everyWeek';

export function mapNotificationFrequency(value: TNotificationFrequencyValue): TNotificationFrequency {
  const notificationMap: { [key: number]: TNotificationFrequency } = {
    1: 'everyHour',
    2: 'everyDay',
    3: 'everyWeek',
  };

  return notificationMap[value];
}

/**
 * Название события, которое отправляем в dataLayer.push,
 * для показа системного попапа разрешения пуш уведомлений
 */

const fakeEmailRex = /@social\.cian\.ru$/;
export const NOTIFICATION_FREQUENCY = [
  { value: 1, label: 'Каждый час' },
  { value: 2, label: 'Каждый день' },
  { value: 3, label: 'Раз в неделю' },
];

export class SavePopup extends React.Component<ISavePopupProps, ISavePopupState> {
  public constructor(props: ISavePopupProps) {
    super(props);

    this.state = {
      title: props.metaHeader || props.pageHeader || '',
      titleInvalid: false,
      email: props.userEmail && !fakeEmailRex.test(props.userEmail) ? props.userEmail : '',
      emailInvalid: false,
      emailError: '',
      isNews: true,
      isLoading: false,
      isTooltipOpen: false,
      isSuccess: false,
      isModalVisible: false,
      isSubscribed: true,
      isPushCheck: true,
      /**
       * По умолчанию частота уведомлений каждый час
       */
      notificationFrequency: 1,
    };
  }

  public pushManager = new PushManager({
    baseUrl: this.props.baseUrl,
    hasSubdomain: this.props.currentSubdomain !== 'www',
  });

  private setSubscriptionHandler = (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);

      if (data.type === EMessageResponseTypes.subscriptionStatus && !data.value) {
        this.setState({ isSubscribed: data.value });
      }
    } catch (e) {}
  };

  public componentDidMount() {
    if (this.props.isAdvancedFiltersOpened) {
      trackSaveButtonWithAdvancedFilterClick();
    }

    this.pushManager.registerWorker();
    PushManager.ready().then(PushManager.checkSubscription);

    window.addEventListener('message', this.setSubscriptionHandler);
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.setSubscriptionHandler);
  }

  public render() {
    const {
      title,
      titleInvalid,
      email,
      emailInvalid,
      isLoading,
      isNews,
      isPushCheck,
      isTooltipOpen,
      isModalVisible,
      isSubscribed,
    } = this.state;
    const { isPushEnabled, isAuthenticated, userEmail, onClose, notificationPermission } = this.props;

    if (isModalVisible) {
      return (
        <SavedSearchModal
          email={email}
          userEmail={userEmail}
          isAuthenticated={isAuthenticated}
          onClose={onClose}
          setVisibility={this.setModalVisible}
        />
      );
    }

    return (
      <ModalWrapper onClose={() => !isLoading && this.handlePopupClose()}>
        <Popup containerStyle={style['popup']}>
          <CloseBtn onClick={this.handlePopupClose} />

          <h3 className={style['title']}>Сохранение поиска</h3>

          <form className={style['form']} action="#" onSubmit={this.handleFormSubmit}>
            <Label
              containerStyle={style['field']}
              labelWrapperStyle={style['label-container']}
              labelStyle={style['label']}
              label="Название поиска"
            >
              <TextField
                value={title}
                disabled={isLoading}
                invalid={titleInvalid}
                onValueChange={this.handleNameChange}
              />
            </Label>
            <Label
              labelWrapperStyle={style['label-container']}
              containerStyle={
                mergeStyles(
                  style['field'],
                  style['field-mail'],
                  // tslint:disable-next-line: no-any
                ) as any
              }
              labelStyle={style['label']}
              label={
                <EmailTooltip
                  isTooltipOpen={isTooltipOpen}
                  handleTooltipePopupClose={this.handleTooltipePopupClose}
                  handleTooltipIconClick={this.handleTooltipIconClick}
                />
              }
            >
              <Tooltip tooltipContent={this.state.emailError} position={TooltipPosition.RIGHT_CENTER}>
                <TextField
                  placeholder="Email"
                  value={email}
                  disabled={isLoading}
                  invalid={emailInvalid}
                  onValueChange={this.handleEmailChange}
                />
              </Tooltip>
            </Label>
            <Label
              containerStyle={style['field']}
              labelWrapperStyle={style['label-container']}
              labelStyle={style['label-notification']}
              label={['Частота', <br />, 'уведомлений']}
            >
              <DropdownField
                value={this.state.notificationFrequency}
                label={getFormattedLabel(this.state.notificationFrequency)}
                onValueChange={(value: TNotificationFrequencyValue) => this.setState({ notificationFrequency: value })}
                values={NOTIFICATION_FREQUENCY}
                containerStyle={style['subscription-period']}
                popupStyle={style['popup-period']}
              />
            </Label>
            <Checkbox
              containerStyle={style['checkbox']}
              label="Хочу получать новости Cian.ru "
              value={isNews}
              onValueChange={value => this.setState({ isNews: value })}
            />
            {isPushEnabled && !isSubscribed && notificationPermission !== 'blocked' && (
              <Checkbox
                containerStyle={style['checkbox']}
                label="Включить push-уведомления"
                value={isPushCheck}
                onValueChange={value => this.setState({ isPushCheck: value })}
              />
            )}
            <div className={style['footer']}>
              <Button theme="primary" type="submit" className={style['button']} disabled={isLoading}>
                Сохранить
              </Button>
              {!this.props.isAuthenticated && <License />}
            </div>
          </form>
        </Popup>
      </ModalWrapper>
    );
  }

  private setModalVisible = (isModalVisible: boolean) => {
    this.setState({ isModalVisible });
  };

  private handleTooltipIconClick = () => {
    this.setState({
      isTooltipOpen: true,
    });
  };

  private handleTooltipePopupClose = () => {
    this.setState({
      isTooltipOpen: false,
    });
  };

  private handleNameChange = (value: string) => {
    this.setState({
      title: value,
      titleInvalid: false,
    });
  };

  private handleEmailChange = (value: string) => {
    this.setState({
      email: value,
      emailInvalid: false,
      emailError: '',
    });
  };

  private getEmailError = () => {
    const { email } = this.state;

    if (!email.trim()) {
      return 'Введите email';
    } else if (!validateEmailFormat(email)) {
      return 'Неверный e-mail адрес';
    }

    return '';
  };

  private handlePopupClose = () => {
    this.props.onClose();

    this.setState({
      emailInvalid: false,
      isLoading: false,
      isTooltipOpen: false,
      isSuccess: false,
    });
  };

  private handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackingSaveFilterSend(this.props.jsonQuery);

    const { currentSubdomain } = this.props;
    const { title } = this.state;

    this.setState({
      isLoading: true,
    });

    const emailError = this.getEmailError();

    if (emailError || !title) {
      this.setState({
        titleInvalid: !title,
        emailInvalid: Boolean(emailError),
        emailError,
        isLoading: false,
      });

      return;
    }

    try {
      await this.saveFilter();
      const { isSubscribed, notificationFrequency, isPushCheck } = this.state;
      const {
        isPushEnabled,
        httpApi,
        applicationServerKey,
        showPushNotification,
        isAdvancedFiltersOpened,
      } = this.props;

      trackSubscribed(notificationFrequency, Boolean(showPushNotification), isPushCheck, isAdvancedFiltersOpened);

      this.setState({ isSuccess: true, isModalVisible: true });

      if (isPushEnabled && !isSubscribed) {
        if (currentSubdomain !== 'www') {
          this.pushManager.showPopup({
            baseUrl: 'web-push-subscribe-popup',
            trackingLabel: 'From_cat_save',
          });
          this.setState({
            isLoading: false,
            isModalVisible: true,
            isSubscribed: true,
          });
        } else if (applicationServerKey && httpApi) {
          const popup = new PopupManager({
            workerSrc: '/push-worker.js',
            applicationServerKey,
            httpApi,
          });
          try {
            await popup.register();
          } catch (error) {
            return;
          }

          try {
            await popup.subscribe();
          } catch (error) {
            return;
          }
        }
      }
    } catch (e) {
      this.setState({
        isLoading: false,
      });
    } finally {
      if (window.__reloadHeader__) {
        window.__reloadHeader__();
      }
    }
  };

  private saveFilter() {
    const { httpApi, jsonQuery } = this.props;
    const { title, email, isNews, notificationFrequency } = this.state;

    return requestSubscribeNew(httpApi, {
      email,
      jsonQuery,
      subscribeNews: isNews,
      title,
      notificationFrequency: mapNotificationFrequency(notificationFrequency),
    }).then((res: IRequestSubscribeNewResponse) => {
      if (isNews) {
        trackingSubscribeWithFlag();
      }

      if (res.auth) {
        trackNewAccountRegistration();
      }
    });
  }
}

function getFormattedLabel(index: number): string {
  const valueObject = NOTIFICATION_FREQUENCY[index - 1];

  if (valueObject) {
    const originLabel = valueObject.label;

    return `Получать ${originLabel.toLowerCase()}`;
  }

  return '';
}
